<template>
  <div>
    <v-container fluid class="px-6 py-6">
      <v-row>
        <v-col cols="12">
          <v-card class="bg-gradient-primary border-radius-xl">
            <div class="px-4 py-4">
              <v-row>
                <v-col cols="8" class="my-auto">
                  <div class="numbers">
                    <p class="text-white text-sm mb-3 text-capitalize font-weight-bold opacity-7">
                      Manufacturing Metrics
                    </p>
                    <h5 class="text-white text-h5 font-weight-light mb-1">
                      Overall Operational Efficiency Dashboard
                    </h5>
                  </div>
                </v-col>
                <v-col cols="4" class="text-end">
                  <h5 class="mb-0 text-white text-h5 text-end me-1">
                    Current OEE: <strong>{{ currentOEE }}%</strong>
                  </h5>
                </v-col>
              </v-row>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <v-col cols="12">
          <v-card class="card-shadow border-radius-xl">
            <div class="pb-0 px-4 pt-4">
              <div class="d-flex align-items-center">
                <h6 class="text-h6 text-typo mb-0 font-weight-bold">
                  Weekly OEE Trend
                </h6>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      rounded
                      v-bind="attrs"
                      v-on="on"
                      min-width="25"
                      elevation="0"
                      :ripple="false"
                      height="25"
                      width="25"
                      class="font-weight-600 text-capitalize btn-outline-secondary py-3 px-4 ms-auto"
                      color="transparent"
                    >
                      <v-icon size="8">fas fa-info</v-icon>
                    </v-btn>
                  </template>
                  <span>Weekly Overall Operational Efficiency shows production line performance over time</span>
                </v-tooltip>
              </div>
            </div>
            <div class="px-4 py-4 position-relative" style="height: 400px">
              <canvas ref="oeeChart" class="chart-canvas" height="300"></canvas>
              
              <!-- Critical Downtime Event marker overlay -->
              <div v-if="showReLevel" class="re-level-indicator" :style="`left: ${criticalEventPosition}%`">
                <div class="re-level-line"></div>
                <div class="re-level-label">
                  Critical Downtime Event
                </div>
              </div>
            </div>
            
            <!-- Time Range Slider -->
            <div class="px-4 pb-4">
              <div class="d-flex align-items-center mb-2 mt-2">
                <span class="text-sm me-2">Time Range:</span>
                <span class="text-sm font-weight-bold">{{ timeRangeLabels[timeRangeSlider[0]] }}</span>
                <span class="text-sm mx-2">to</span>
                <span class="text-sm font-weight-bold">{{ timeRangeLabels[timeRangeSlider[1]] }}</span>
              </div>
              <v-range-slider
                v-model="timeRangeSlider"
                min="0"
                :max="timeRangeLabels.length - 1"
                class="mt-0"
                hide-details
                @change="updateTimeRange"
              ></v-range-slider>
              
              <div class="d-flex justify-space-between mt-1">
                <span v-for="(label, index) in timeRangeLabels" :key="index" class="text-xs">
                  {{ index % 3 === 0 ? label : '' }}
                </span>
              </div>
              
              <div class="d-flex justify-end mt-4">
                <v-btn dark color="primary" small @click="playTimeSeriesAnimation">
                  <v-icon  small class="me-1">fas fa-play</v-icon>
                  Animate
                </v-btn>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <v-col cols="4">
          <v-card class="card-shadow border-radius-xl">
            <div class="px-4 py-4">
              <h6 class="text-h6 text-typo font-weight-bold mb-3">
                OEE Status
              </h6>
              <div class="d-flex align-items-center">
                <div :class="`status-indicator ${oeeStatus.color}`"></div>
                <h4 class="text-h4 font-weight-bold ms-3 mb-0">{{ oeeStatus.label }}</h4>
              </div>
              <p class="text-body mt-3 mb-0">{{ oeeStatus.description }}</p>
            </div>
          </v-card>
        </v-col>
        <v-col cols="8">
          <v-card class="card-shadow border-radius-xl">
            <div class="px-4 py-4">
              <h6 class="text-h6 text-typo font-weight-bold mb-3">
                Downtime Incidents
              </h6>
              <v-list v-if="downtimeIncidents.length > 0" dense>
                <v-list-item v-for="(incident, index) in downtimeIncidents" :key="index">
                  <v-list-item-icon>
                    <v-icon color="error">mdi-alert-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ incident.date }}</v-list-item-title>
                    <v-list-item-subtitle>{{ incident.reason }} - {{ incident.duration }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <span class="text-error">{{ incident.oee }}% OEE</span>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <div v-else class="text-center py-4">
                <p class="mb-0">No downtime incidents in the selected period</p>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';

export default {
  name: "widgets",
  data() {
    return {
      oeeChart: null,
      currentOEE: 94,
      showReLevel: true,
      criticalEventPosition: 58.7, // Position from left as percentage (aligns with lowest point)
      timeRangeSlider: [0, 12], // Default to show all data
      timeRangeLabels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'],
      fullChartData: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'],
        datasets: [{
          label: 'Weekly OEE',
          data: [90, 88, 85, 90, 77, 67, 71, 45, 90, 89, 92, 93, 94],
          borderColor: '#3A416F',
          backgroundColor: 'transparent',
          tension: 0, // Sharp lines instead of curved
          pointRadius: 4,
          borderWidth: 3,
        }]
      },
      chartData: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'],
        datasets: [{
          label: 'Weekly OEE',
          data: [90, 88, 85, 90, 77, 67, 71, 45, 90, 89, 92, 93, 94],
          borderColor: '#3A416F',
          backgroundColor: 'transparent',
          tension: 0, // Sharp lines instead of curved
          pointRadius: 4,
          borderWidth: 3,
        }]
      },
      downtimeIncidents: [
        {
          date: 'August 15, 2018',
          reason: 'Machine Alignment Issue',
          duration: '3 days',
          oee: 45
        },
        {
          date: 'July 5, 2018',
          reason: 'Unplanned Maintenance',
          duration: '1 day',
          oee: 67
        }
      ],
      animationInterval: null,
      animationCurrentIndex: 0
    };
  },
  computed: {
    oeeStatus() {
      const oee = this.currentOEE;
      if (oee >= 85) {
        return {
          label: 'Good',
          color: 'status-green',
          description: 'OEE is within optimal production parameters.'
        };
      } else if (oee >= 70) {
        return {
          label: 'Warning',
          color: 'status-yellow',
          description: 'OEE is below target but within acceptable range.'
        };
      } else {
        return {
          label: 'Critical',
          color: 'status-red',
          description: 'OEE is significantly below target. Immediate attention required.'
        };
      }
    }
  },
  beforeDestroy() {
    if (this.animationInterval) {
      clearInterval(this.animationInterval);
    }
  },
  mounted() {
    this.initOEEChart();
    this.startRealTimeUpdates();
  },
  methods: {
    initOEEChart() {
      const ctx = this.$refs.oeeChart.getContext('2d');
      
      // Define the critical threshold line
      const criticalThreshold = {
        id: 'criticalThreshold',
        beforeDraw(chart) {
          const { ctx, chartArea, scales } = chart;
          const yScale = scales.y;
          const criticalValue = 70; // OEE value below which is considered critical
          
          const y = yScale.getPixelForValue(criticalValue);
          
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(chartArea.left, y);
          ctx.lineTo(chartArea.right, y);
          ctx.lineWidth = 1;
          ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
          ctx.setLineDash([5, 5]);
          ctx.stroke();
          
          // Add label
          ctx.fillStyle = 'rgba(255, 0, 0, 0.8)';
          ctx.fillText('Critical Threshold', chartArea.left + 10, y - 5);
          ctx.restore();
        }
      };
      
      this.oeeChart = new Chart(ctx, {
        type: 'line',
        data: this.chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || '';
                  if (label) {
                    label += ': ';
                  }
                  if (context.parsed.y !== null) {
                    label += context.parsed.y + '%';
                  }
                  return label;
                }
              }
            }
          },
          scales: {
            y: {
              min: 0,
              max: 100,
              grid: {
                drawBorder: false,
                borderDash: [5, 5],
              },
              ticks: {
                callback: function(value) {
                  return value + '%';
                }
              }
            },
            x: {
              grid: {
                drawBorder: false,
                borderDash: [5, 5],
              }
            }
          },
          elements: {
            point: {
              backgroundColor: function(context) {
                const value = context.raw;
                if (value < 70) {
                  return '#FF5252'; // Red for critical points
                }
                return '#3A416F'; // Default color
              },
              radius: function(context) {
                const value = context.raw;
                if (value < 70) {
                  return 6; // Larger radius for critical points
                }
                return 4; // Default radius
              }
            }
          }
        },
        plugins: [criticalThreshold]
      });
    },
    startRealTimeUpdates() {
      // Simulate real-time data updates
      setInterval(() => {
        if (!this.animationInterval) { // Only update if not in animation mode
          const data = this.oeeChart.data.datasets[0].data;
          const fluctuation = Math.random() * 2 - 1; // Random fluctuation between -1 and 1
          
          // Update the last data point with a small random change
          const lastValue = data[data.length - 1];
          let newValue = lastValue + fluctuation;
          
          // Keep within reasonable bounds
          newValue = Math.max(Math.min(newValue, 97), 85);
          
          data[data.length - 1] = Math.round(newValue);
          this.currentOEE = Math.round(newValue);
          
          this.oeeChart.update('none'); // Update without animation for smoother real-time effect
        }
      }, 2000);
    },
    updateTimeRange() {
      // Get selected range
      const start = this.timeRangeSlider[0];
      const end = this.timeRangeSlider[1];
      
      // Update chart data with selected range
      this.chartData.labels = this.fullChartData.labels.slice(start, end + 1);
      this.chartData.datasets[0].data = this.fullChartData.datasets[0].data.slice(start, end + 1);
      
      // Check if critical point (Aug with 45% OEE) is in the range
      const augustIndex = 7; // 0-based index for August
      this.showReLevel = augustIndex >= start && augustIndex <= end;
      
      // Calculate position of critical event marker relative to the visible range
      if (this.showReLevel) {
        // Calculate position as percentage of visible range width
        const rangeWidth = end - start;
        const relativePosition = (augustIndex - start) / rangeWidth;
        this.criticalEventPosition = relativePosition * 100;
      }
      
      // Update chart
      if (this.oeeChart) {
        this.oeeChart.update();
      }
    },
    playTimeSeriesAnimation() {
      // Clear any existing animation
      if (this.animationInterval) {
        clearInterval(this.animationInterval);
        this.animationInterval = null;
        // Reset to full range
        this.timeRangeSlider = [0, 12];
        this.updateTimeRange();
        return;
      }
      
      // Start with just the first data point
      this.animationCurrentIndex = 0;
      this.timeRangeSlider = [0, 0];
      this.updateTimeRange();
      
      // Animate by progressively showing more data points
      this.animationInterval = setInterval(() => {
        this.animationCurrentIndex++;
        
        if (this.animationCurrentIndex >= this.fullChartData.labels.length) {
          // Animation complete, stop interval
          clearInterval(this.animationInterval);
          this.animationInterval = null;
          return;
        }
        
        // Update slider to show points from 0 to current index
        this.timeRangeSlider = [0, this.animationCurrentIndex];
        this.updateTimeRange();
        
        // Update current OEE to match the latest visible data point
        const latestData = this.chartData.datasets[0].data;
        this.currentOEE = latestData[latestData.length - 1];
      }, 500); // Advance every 500ms
    }
  }
};
</script>

<style scoped>
.status-indicator {
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

.status-green {
  background-color: #4CAF50;
}

.status-yellow {
  background-color: #FFC107;
}

.status-red {
  background-color: #F44336;
}

.re-level-indicator {
  position: absolute;
  top: 50px;
  height: 70%;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
  z-index: 10;
  transition: left 0.3s ease;
}

.re-level-line {
  width: 3px;
  height: 80%;
  background-color: #F44336;
}

.re-level-label {
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  color: black;
  font-weight: bold;
  text-align: center;
}

.re-level-text {
  line-height: 1.2;
}
</style> 